/**
 * Debug API exposed on `window.ROVO_NUDGE_SUPPRESSION` for inspecting nudge suppression state.
 *
 * Usage:
 *   window.ROVO_NUDGE_SUPPRESSION.list()
 *   window.ROVO_NUDGE_SUPPRESSION.isSuppressed('nudge-id', 'optional-key')
 *   window.ROVO_NUDGE_SUPPRESSION.clearAll()
 *   window.ROVO_NUDGE_SUPPRESSION.clearNudge('nudge-id')
 *   window.ROVO_NUDGE_SUPPRESSION.showAllowlist()
 */

import { isBrowser } from '../../is-browser';
import { SUPPRESSION_ALLOWLIST } from '../suppression-allowlist';
import {
	clearAllSuppression,
	clearSuppression,
	readSuppressionMap,
} from '../suppression-store';

export type SuppressionDebugAPI = {
	/** Clear all suppression entries for the current cloudId. */
	clearAll(): void;
	/** Clear suppression entries whose key starts with the given nudgeId. */
	clearNudge(nudgeId: string): void;
	/** Check if a specific (nudgeId, optionalKeyPart) is currently suppressed. */
	isSuppressed(nudgeId: string, keyPart?: string): boolean;
	/** List all suppressed nudges with their expiry timestamps. */
	list(): Array<{ expiresAt: number; expiresAtISO: string; key: string; }>;
	/** Print the active allowlist to the console. */
	showAllowlist(): void;
};

declare global {
	// eslint-disable-next-line no-var
	var ROVO_NUDGE_SUPPRESSION: SuppressionDebugAPI | undefined;
}

/**
 * Register the debug API on `window`.
 * The scope is bound to the cloudId of the current Confluence/Jira tenant.
 */
export const registerSuppressionDebugAPI = (): void => {
	if (!isBrowser()) {
		return;
	}

	window.ROVO_NUDGE_SUPPRESSION = {
		list: () => {
			const now = Date.now();
			const entries = readSuppressionMap();
			return Array.from(entries.entries())
				.filter(([, expiresAt]) => expiresAt > now)
				.map(([key, expiresAt]) => ({
					key,
					expiresAt,
					expiresAtISO: new Date(expiresAt).toISOString(),
				}));
		},
		isSuppressed: (nudgeId: string, keyPart?: string): boolean => {
			const fullKey = keyPart ? `${nudgeId}:${keyPart}` : nudgeId;
			const entries = readSuppressionMap();
			const expiresAt = entries.get(fullKey);
			return expiresAt !== undefined && expiresAt > Date.now();
		},
		clearAll: () => {
			clearAllSuppression();
		},
		clearNudge: (nudgeId: string) => {
			const entries = readSuppressionMap();
			Array.from(entries.keys())
				.filter((key) => key === nudgeId || key.startsWith(`${nudgeId}:`))
				.forEach((key) => clearSuppression(key));
		},
		showAllowlist: () => {
			// eslint-disable-next-line no-console
			console.table(
				Array.from(SUPPRESSION_ALLOWLIST).map((id) => ({ id })),
			);
		},
	};
};

export const unregisterSuppressionDebugAPI = (): void => {
	if (!isBrowser()) {
		return;
	}
	if (window.ROVO_NUDGE_SUPPRESSION) {
		delete window.ROVO_NUDGE_SUPPRESSION;
	}
};
